import {isDomesticRegistrationOksm, isRussiaRegistrationOksm} from './countries';
import {RegistrationPaymentMethod} from './paymentMethod';
import {hasSavedSnils} from './snils';
import {RegistrationWorkStatus} from './types';
import {isFinance, isRegularLkHost, isYpla} from 'shared/lib/isFinance';

export type TaxIdType = 'russian_inn' | 'foreign_tax_id';

/** Экран: не показываем / показываем (можно пропустить) / показываем и требуем заполнить */
export type FieldRequirement = 'hidden' | 'optional' | 'mandatory';

export type RegistrationBackendFlags = {
	snilsRequired?: boolean | null;
	residenceCodeRequired?: boolean | null;
	/** false — POST /identification/identity_verification недоступен, шаг сверки пропускаем. */
	identityVerificationRequired?: boolean | null;
};

export type RegistrationFlowInput = {
	citizenshipOksmCode: string | null;
	residenceOksmCode: string | null;
	workStatus: RegistrationWorkStatus | null;
	backendFlags?: RegistrationBackendFlags;
};

export type RegistrationFlowConfig = {
	allowedWorkStatuses: RegistrationWorkStatus[];
	taxIdType: TaxIdType;
	snils: FieldRequirement;
	residentCode: FieldRequirement;
	paymentMethods: RegistrationPaymentMethod[];
};

const ALL_WORK_STATUSES: RegistrationWorkStatus[] = [
	'self_employment',
	'individual',
	'individual_entrepreneur',
];

const INDIVIDUAL_AND_IE: RegistrationWorkStatus[] = ['individual', 'individual_entrepreneur'];
const INDIVIDUAL_ONLY: RegistrationWorkStatus[] = ['individual'];

const PAYMENT_SBP: RegistrationPaymentMethod = 'sbp_account';
const PAYMENT_CARD: RegistrationPaymentMethod = 'bank_card';
const PAYMENT_ACCOUNT: RegistrationPaymentMethod = 'executor_account';
const PAYMENT_IBAN: RegistrationPaymentMethod = 'executor_account/swift';

const PAYMENTS_INDIVIDUAL_RF = [PAYMENT_SBP, PAYMENT_ACCOUNT, PAYMENT_CARD];
const PAYMENTS_INDIVIDUAL_ABROAD = [PAYMENT_SBP, PAYMENT_ACCOUNT, PAYMENT_CARD, PAYMENT_IBAN];
const PAYMENTS_IE = [PAYMENT_ACCOUNT];

type ProfileKey = `${'ru' | 'foreign'}_${'ru' | 'foreign'}`;

const getProfileKey = (citizenshipOksmCode: string | null, residenceOksmCode: string | null): ProfileKey => {
	const citizenship = isDomesticRegistrationOksm(citizenshipOksmCode) ? 'ru' : 'foreign';
	const residence = isDomesticRegistrationOksm(residenceOksmCode) ? 'ru' : 'foreign';

	return `${citizenship}_${residence}`;
};

type ProfileDefaults = {
	allowedWorkStatuses: RegistrationWorkStatus[];
	taxIdType: TaxIdType;
	snilsByWorkStatus: Record<RegistrationWorkStatus, FieldRequirement>;
	residentCodeByWorkStatus: Record<RegistrationWorkStatus, FieldRequirement>;
	paymentMethodsByWorkStatus: Record<RegistrationWorkStatus, RegistrationPaymentMethod[]>;
};

const HIDDEN: FieldRequirement = 'hidden';
const OPTIONAL: FieldRequirement = 'optional';
const MANDATORY: FieldRequirement = 'mandatory';

const PROFILE_DEFAULTS: Record<ProfileKey, ProfileDefaults> = {
	ru_ru: {
		allowedWorkStatuses: ALL_WORK_STATUSES,
		taxIdType: 'russian_inn',
		snilsByWorkStatus: {
			self_employment: HIDDEN,
			individual: MANDATORY,
			individual_entrepreneur: HIDDEN,
		},
		residentCodeByWorkStatus: {
			self_employment: HIDDEN,
			individual: OPTIONAL,
			individual_entrepreneur: HIDDEN,
		},
		paymentMethodsByWorkStatus: {
			self_employment: PAYMENTS_INDIVIDUAL_RF,
			individual: PAYMENTS_INDIVIDUAL_RF,
			individual_entrepreneur: PAYMENTS_IE,
		},
	},
	ru_foreign: {
		allowedWorkStatuses: INDIVIDUAL_AND_IE,
		taxIdType: 'foreign_tax_id',
		snilsByWorkStatus: {
			self_employment: HIDDEN,
			individual: HIDDEN,
			individual_entrepreneur: HIDDEN,
		},
		residentCodeByWorkStatus: {
			self_employment: HIDDEN,
			individual: HIDDEN,
			individual_entrepreneur: HIDDEN,
		},
		paymentMethodsByWorkStatus: {
			self_employment: PAYMENTS_INDIVIDUAL_ABROAD,
			individual: PAYMENTS_INDIVIDUAL_ABROAD,
			individual_entrepreneur: PAYMENTS_IE,
		},
	},
	foreign_ru: {
		allowedWorkStatuses: ALL_WORK_STATUSES,
		taxIdType: 'russian_inn',
		snilsByWorkStatus: {
			self_employment: HIDDEN,
			individual: MANDATORY,
			individual_entrepreneur: HIDDEN,
		},
		residentCodeByWorkStatus: {
			self_employment: HIDDEN,
			individual: OPTIONAL,
			individual_entrepreneur: HIDDEN,
		},
		paymentMethodsByWorkStatus: {
			self_employment: PAYMENTS_INDIVIDUAL_RF,
			individual: PAYMENTS_INDIVIDUAL_RF,
			individual_entrepreneur: PAYMENTS_IE,
		},
	},
	foreign_foreign: {
		allowedWorkStatuses: INDIVIDUAL_ONLY,
		taxIdType: 'foreign_tax_id',
		snilsByWorkStatus: {
			self_employment: HIDDEN,
			individual: HIDDEN,
			individual_entrepreneur: HIDDEN,
		},
		residentCodeByWorkStatus: {
			self_employment: HIDDEN,
			individual: HIDDEN,
			individual_entrepreneur: HIDDEN,
		},
		paymentMethodsByWorkStatus: {
			self_employment: PAYMENTS_INDIVIDUAL_ABROAD,
			individual: PAYMENTS_INDIVIDUAL_ABROAD,
			individual_entrepreneur: PAYMENTS_IE,
		},
	},
};

const resolveFieldRequirement = (
	requirement: FieldRequirement,
	backendFlag?: boolean | null,
): FieldRequirement => {
	if (requirement === MANDATORY) {
		return MANDATORY;
	}

	// optional в профиле — экран есть (можно пропустить); бэк может сделать обязательным.
	if (requirement === OPTIONAL) {
		return backendFlag === true ? MANDATORY : OPTIONAL;
	}

	// hidden — показать только если бэк явно попросил.
	return backendFlag ? MANDATORY : HIDDEN;
};

export const extractRegistrationBackendFlags = (
	fullInfo: {info?: unknown} | null | undefined,
): RegistrationBackendFlags => {
	if (!fullInfo?.info || typeof fullInfo.info !== 'object') {
		return {};
	}

	const info = fullInfo.info as RegistrationBackendFlags & Record<string, unknown>;

	return {
		snilsRequired: info.snilsRequired ?? null,
		residenceCodeRequired: info.residenceCodeRequired ?? null,
		identityVerificationRequired: info.identityVerificationRequired ?? null,
	};
};

/** Бэк прислал `identityVerificationRequired: false` — идентификацию не вызываем. */
export const shouldSkipIdentityVerification = (flags: RegistrationBackendFlags | undefined): boolean =>
	flags?.identityVerificationRequired === false;

export const resolveRegistrationFlow = (input: RegistrationFlowInput): RegistrationFlowConfig => {
	const profile = PROFILE_DEFAULTS[getProfileKey(input.citizenshipOksmCode, input.residenceOksmCode)];
	const workStatus = input.workStatus ?? 'individual';
	const snilsBase = profile.snilsByWorkStatus[workStatus];
	const residentCodeBase = profile.residentCodeByWorkStatus[workStatus];

	let snils =
		workStatus === 'self_employment'
			? resolveFieldRequirement(snilsBase, input.backendFlags?.snilsRequired)
			: resolveFieldRequirement(snilsBase);

	let residentCode = resolveFieldRequirement(residentCodeBase, input.backendFlags?.residenceCodeRequired);

	// Физлицо с иностранным Tax ID: в подключении статуса только налоговый идентификатор.
	if (profile.taxIdType === 'foreign_tax_id' && workStatus === 'individual') {
		snils = HIDDEN;
		residentCode = HIDDEN;
	}

	// Finance и YPLA СМЗ: ИНН + СНИЛС, интеграция Альфа-банк.
	// `lk.rocketwork.finance` идёт как обычный ЛК (rocketwork).
	if (!isRegularLkHost() && (isFinance() || isYpla()) && workStatus === 'self_employment') {
		snils = MANDATORY;
		residentCode = HIDDEN;
	}

	// «Введите ИНН» только при резидентстве РФ. Беларусь идёт domestic-сценарием, но ИНН РФ ей не нужен.
	const taxIdType: TaxIdType =
		profile.taxIdType === 'russian_inn' && !isRussiaRegistrationOksm(input.residenceOksmCode)
			? 'foreign_tax_id'
			: profile.taxIdType;

	return {
		allowedWorkStatuses: profile.allowedWorkStatuses,
		taxIdType,
		snils,
		residentCode,
		paymentMethods: profile.paymentMethodsByWorkStatus[workStatus],
	};
};

export const isRegistrationStepRequired = (requirement: FieldRequirement): boolean => {
	return requirement === MANDATORY;
};

export const isRegistrationStepVisible = (requirement: FieldRequirement): boolean => {
	return requirement !== HIDDEN;
};

/**
 * Спрашивать СНИЛС, только если шаг виден в конфиге и у исполнителя его ещё нет.
 * У СМЗ видимость — это `snilsRequired: true`.
 */
export const shouldRequestSnilsStep = (
	requirement: FieldRequirement,
	snils: string | null | undefined,
): boolean => {
	return isRegistrationStepVisible(requirement) && !hasSavedSnils(snils);
};

/** Включён — шаг оплаты при подключении статуса ИП. */
export const STATUS_CONNECTION_PAYMENT_ENABLED = true;

/** Способы оплаты при подключении статуса. */
export const getStatusConnectionPaymentMethods = (
	workStatus: RegistrationWorkStatus | null,
	paymentMethods: RegistrationPaymentMethod[] = [],
): RegistrationPaymentMethod[] => {
	if (!STATUS_CONNECTION_PAYMENT_ENABLED) {
		return [];
	}

	if (workStatus === 'individual_entrepreneur') {
		return [PAYMENT_ACCOUNT];
	}

	return [];
};
